import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { shopperHeaders } from '@/lib/shopper';

// بعد الدخول: لو سلة/مفضلة الزائر اتنقلت للحساب نوضّح ده للعميلة
const notifyMerged = (merged) => {
  const parts = [];
  if (merged?.cart > 0) parts.push(`${merged.cart} من سلتك`);
  if (merged?.wishlist > 0) parts.push(`${merged.wishlist} من مفضلتك`);
  if (parts.length) toast.success('تم حفظ اختياراتك في حسابك', { description: `تم نقل ${parts.join(' و')}` });
};

axios.defaults.headers.common['Accept'] = 'application/json';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    // لو السيرفر مش متاح مؤقتاً نفضل محتفظين بالتوكن ونعيد المحاولة،
    // ونخرج المستخدم فقط لو السيرفر رد صراحةً إن التوكن غير صالح (401)
    let cancelled = false;
    let retryTimer;
    let attempt = 0;

    const load = async () => {
      const result = await fetchUser();
      if (cancelled || result !== 'failed') return;
      attempt += 1;
      retryTimer = setTimeout(load, Math.min(15000, 2000 * attempt));
    };
    load();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [token]);

  const clearSession = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    setLoading(false);
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.data);
      setLoading(false);
      return 'ok';
    } catch (error) {
      if (error.response?.status === 401) {
        clearSession();
        return 'unauthorized';
      }
      console.error('Failed to fetch user, will retry:', error);
      return 'failed';
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { headers: shopperHeaders() });
    const { access_token, user, merged } = response.data.data;
    setToken(access_token);
    setUser(user);
    localStorage.setItem('token', access_token);
    notifyMerged(merged);
    return response.data;
  };

  const register = async (email, full_name, phone, password) => {
    // Backend expects 'name', so we map 'full_name' to 'name'
    const payload = { email, name: full_name, phone, password };
    const response = await axios.post(`${API}/auth/register`, payload, { headers: shopperHeaders() });
    const { access_token, user, merged } = response.data.data;
    setToken(access_token);
    setUser(user);
    localStorage.setItem('token', access_token);
    notifyMerged(merged);
    return response.data;
  };

  const logout = async () => {
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    clearSession();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
